import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import PinIcon from '@src/svgs/PinIcon';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import DirectedPreviewButton from './DirectedPreviewButton';
import LanguageSamplePlayButton from './LanguageSamplePlayButton';
import { explainVoiceForRun } from './resolveVoiceForRun';
import { samplePlayCss } from './samplePlayStyles';
import { useSpeakerAiVoices } from './useSpeakerAiVoices';
import { voiceSpeaksLanguage } from './voiceLanguageDefaults';
import { EmptyState } from './voiceMapperStyles';
import VoiceSelect from './VoiceSelect';

import type { DirectionPreview } from '@src/services/dubbing/directionPreview';
import type { SpeakerWithColor } from '@src/services/SpeakerManagementService';
import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';
import type { Language } from '@src/types.ts/language';

const DUB_ROW_TOOLTIP_ID = 'dub-run-voice-row-tooltip';

/** A transcript voice (`spk_X`) with no DatoCMS speaker behind it yet. */
export interface UnassignedTranscriptVoice {
  /** The transcript label the manifest keys its segments on, e.g. `spk_0`. */
  label: string;
  /** Display name for the label, e.g. "Speaker 1". */
  name: string;
  color: string;
}

export interface DubRunVoiceMapperProps {
  /** Assigned speakers (DatoCMS records) that will be dubbed, one row each. */
  speakers: SpeakerWithColor[];
  /** The run's target language (the selected translation); null while resolving. */
  language: Language | null;
  /**
   * Deliberate per-speaker user picks, keyed by DatoCMS speaker id (PLAT-10727).
   * Only voices the user chose in the picker land here — an auto-resolved
   * default never does, so a later language-default change can re-resolve. Feeds
   * the resolver's tier-1 run override.
   */
  userOverrides: Record<string, string>;
  /** Per-speaker reload counter; a bump re-syncs that row from the cache. */
  reloadTokens: Record<string, number>;
  /** Report a speaker's resolved/overridden voice up (undefined = no voice). */
  onSelect: (speakerId: string, voiceId: string | undefined) => void;
  /** Record a deliberate user pick in the picker (tier-1 override, PLAT-10727). */
  onUserPick: (speakerId: string, voiceId: string) => void;
  /** Open the full Manage AI Voices modal for a speaker (deep management). */
  onManageSpeaker: (speaker: SpeakerWithColor) => void;
  /**
   * Transcript voices with no speaker behind them. Each one has
   * lines the run will not cover, and a speaker is what carries an AI Voice, so
   * they are listed with an assign action rather than left off: when none is
   * assigned they are the only way out of an empty mapper, and when some are
   * they are the only sign that the rest of the transcript goes undubbed.
   */
  unassignedVoices: UnassignedTranscriptVoice[];
  /** Open the assign flow for a transcript voice. */
  onAssignVoice: (voice: UnassignedTranscriptVoice) => void;
  busy: boolean;
  /**
   * Read-only mode (PLAT-10616 span re-dub): the resolved voice per speaker is
   * still shown, but the picker is disabled and "Manage voices" is hidden — the
   * run reuses each speaker's current voice and doesn't allow a change.
   */
  readOnly?: boolean;
  /**
   * PLAT-12023 — what this speaker's play button previews: their words with
   * the run's current direction. Given by the host that knows the direction;
   * without it the button plays the voice's stored sample, as before.
   */
  previewFor?: (speakerId: string) => DirectionPreview | undefined;
}

// The kick-off popover's per-speaker voice mapper (DE5/DI19): one row per
// assigned speaker → the AI Voice to dub them with for the run's language. It
// reuses the PLAT-10501 primitives (VoiceSelect, useSpeakerAiVoices, the DI19
// resolver) rather than the single-speaker three-tab SpeakerAIVoiceMapper — the
// run needs a compact one-row-per-speaker shape, not a management surface.
const DubRunVoiceMapper = ({
  speakers,
  language,
  userOverrides,
  reloadTokens,
  onSelect,
  onUserPick,
  onManageSpeaker,
  unassignedVoices,
  onAssignVoice,
  busy,
  readOnly = false,
  previewFor,
}: DubRunVoiceMapperProps) => {
  // A repair of an existing dub reuses the speakers it already has, so it has no
  // business offering assignment; everywhere else, a transcript voice with no
  // speaker is something the creator can still act on.
  const assignable = readOnly ? [] : unassignedVoices;

  const unassignedList = assignable.length > 0 && (
    <List>
      {assignable.map(voice => (
        <UnassignedRow key={voice.label}>
          <SpeakerAvatar
            name={voice.name}
            color={voice.color}
            size={28}
            initialsLength={2}
          />
          <div className="body">
            <span className="name">{voice.name}</span>
            <span className="hint">Not assigned</span>
          </div>
          <AssignButton
            type="button"
            onClick={() => onAssignVoice(voice)}
            disabled={busy}
          >
            Assign Speaker
          </AssignButton>
        </UnassignedRow>
      ))}
    </List>
  );

  if (speakers.length === 0) {
    // Nothing to dub — but the transcript's own voices are right here, and each
    // is one assignment away from being dubbable, so offer that rather than a
    // sentence telling the creator to go and do it somewhere else.
    if (!unassignedList) {
      return (
        <EmptyState>
          No assigned speakers to dub. Assign a speaker to a transcript voice
          first.
        </EmptyState>
      );
    }
    return (
      <>
        <Lead>
          No speaker is assigned yet, and an AI Voice belongs to a speaker.
          Assign one to each transcript voice you want dubbed.
        </Lead>
        {unassignedList}
      </>
    );
  }

  return (
    <>
      <List>
        {speakers.map(speaker => (
          <DubRunVoiceRow
            key={speaker.id}
            speaker={speaker}
            language={language}
            override={userOverrides[speaker.id]}
            reloadToken={reloadTokens[speaker.id] ?? 0}
            onSelect={onSelect}
            onUserPick={onUserPick}
            onManageSpeaker={onManageSpeaker}
            busy={busy}
            readOnly={readOnly}
            preview={previewFor?.(speaker.id)}
          />
        ))}
      </List>
      {/* Voices still without a speaker, kept in sight below the mapper: the
          run covers only what is listed above, so dropping them would let their
          lines go undubbed with nothing on screen saying so. */}
      {unassignedList && (
        <>
          <Lead $spaced>
            {assignable.length === 1
              ? 'One transcript voice has no speaker yet. Its lines keep the original voice unless you assign one.'
              : `${assignable.length} transcript voices have no speaker yet. Their lines keep the original voice unless you assign one.`}
          </Lead>
          {unassignedList}
        </>
      )}
      <Tooltip id={DUB_ROW_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />
    </>
  );
};

export default DubRunVoiceMapper;

interface DubRunVoiceRowProps {
  speaker: SpeakerWithColor;
  language: Language | null;
  override: string | undefined;
  reloadToken: number;
  onSelect: (speakerId: string, voiceId: string | undefined) => void;
  onUserPick: (speakerId: string, voiceId: string) => void;
  onManageSpeaker: (speaker: SpeakerWithColor) => void;
  busy: boolean;
  readOnly: boolean;
  preview?: DirectionPreview;
}

// One speaker's row. Loads the speaker's associated voices (cache-first via
// DubbingStore) and resolves the run voice per DI19, reporting the effective
// choice up. `observer` so it re-renders when the catalog hydrates.
const DubRunVoiceRow = observer(
  ({
    speaker,
    language,
    override,
    reloadToken,
    onSelect,
    onUserPick,
    onManageSpeaker,
    busy,
    readOnly,
    preview,
  }: DubRunVoiceRowProps) => {
    const dubbingStore = useDubbingStore();
    const {
      aiVoiceIds,
      languagePrefs,
      busy: speakerBusy,
      reload,
      setLanguageDefault,
    } = useSpeakerAiVoices(speaker.id);

    // Re-sync from the cache when this speaker's Manage modal closes (the token
    // bumps): edits there ran through a separate hook instance, so this row's
    // local snapshot is stale until it reloads. Skips the initial mount (0).
    useEffect(() => {
      if (reloadToken > 0) void reload();
    }, [reloadToken, reload]);

    // Resolve the speaker's ordered voice ids to catalog records, dropping any
    // that no longer resolve (mirrors ManageAiVoicesModal).
    const orderedVoices = useMemo(
      () =>
        aiVoiceIds
          .map(id => dubbingStore.voiceById(id))
          .filter((v): v is AiVoiceRecord => Boolean(v)),
      [aiVoiceIds, dubbingStore, dubbingStore.catalog],
    );

    // The run is scoped to the target language, so the picker offers only the
    // speaker's voices that speak it (DE5 refinement — filtered, not flagged).
    // While the language is still resolving, show all.
    const speaking = useMemo(
      () =>
        language
          ? orderedVoices.filter(v => voiceSpeaksLanguage(v, language))
          : orderedVoices,
      [orderedVoices, language],
    );

    const { voiceId: resolvedVoiceId, tier } = explainVoiceForRun({
      orderedVoices,
      languagePrefs,
      languageId: language?.id ?? null,
      runOverride: override,
    });

    // Push the effective voice (default or override) up so the modal can build
    // the request map. Fires whenever the resolution changes (voices hydrate,
    // a user picks, or a voice is assigned) — stable once resolved. This is the
    // effective-selection channel only; a deliberate pick is recorded separately
    // via onUserPick so the auto-seeded default can't masquerade as a tier-1
    // override (PLAT-10727).
    useEffect(() => {
      onSelect(speaker.id, resolvedVoiceId);
    }, [resolvedVoiceId, speaker.id, onSelect]);

    const headshotUrl = speaker.headshots?.[0]?.url ?? null;
    const rowBusy = busy || speakerBusy;
    const languageCaption = language?.caption ?? 'this language';

    // Where the voice in the trigger came from (the decision ladder: this
    // dub's pick, then the speaker's pin, then the first voice in their order
    // that speaks the language), so "why Brian?" is answered on the row.
    const provenance =
      tier &&
      {
        // The badge says what the choice means for the NEXT dub, in the word
        // Manage AI Voices already teaches ("Default"), so the pushpin beside
        // it reads as "make this the default" rather than a new concept.
        override: {
          badge: 'Just this time',
          hint: `Used for this dub only. ${speaker.name}'s default ${languageCaption} voice is unchanged.`,
        },
        pinned: {
          badge: `Default for ${languageCaption}`,
          hint: `${speaker.name}'s default ${languageCaption} voice — used every time ${speaker.name} is dubbed into it.`,
        },
        automatic: {
          badge: 'Auto-picked',
          hint: `No default set for ${languageCaption}, so the first of ${speaker.name}'s voices that speaks it is used.`,
        },
      }[tier];

    // Pin remembers the row's voice as the speaker's voice for the language,
    // so the next dub does not have to choose it again; Unpin forgets it. On
    // every writable row, not only after a pick here — an "Automatic" row is
    // exactly where a person wants to say "keep this one".
    const remembered =
      !!language &&
      !!resolvedVoiceId &&
      languagePrefs[language.id] === resolvedVoiceId;
    const canRemember = !readOnly && !!language && !!resolvedVoiceId;

    return (
      <Row $noVoice={speaking.length === 0}>
        <SpeakerAvatar
          name={speaker.name}
          color={speaker.color}
          headshotUrl={headshotUrl}
          size={28}
          initialsLength={2}
        />
        <div className="body">
          <span className="name">{speaker.name}</span>
          {speaking.length === 0 && (
            <span className="hint">No {languageCaption} AI Voice yet.</span>
          )}
        </div>
        <div className="controls">
          {speaking.length > 0 ? (
            <>
              <div className="select-row">
                <VoiceSelect
                  id={`dub-voice-${speaker.id}`}
                  voices={speaking}
                  value={resolvedVoiceId ?? ''}
                  onChange={voiceId => onUserPick(speaker.id, voiceId)}
                  disabled={rowBusy || readOnly}
                  density="compact"
                  sampleLanguage={language?.value}
                  triggerBadge={provenance?.badge}
                  triggerBadgeHint={provenance?.hint}
                  tooltipId={DUB_ROW_TOOLTIP_ID}
                  // "Manage voices" now lives at the bottom of the picker instead
                  // of a green link repeated under every row (PLAT-10761).
                  onManageVoices={
                    readOnly ? undefined : () => onManageSpeaker(speaker)
                  }
                />
                {/* Pin makes this the speaker's voice for the language from
                    now on (the same pin the "Pinned" badge reports). Beside the
                    picker rather than under it, so the row stays one line. */}
                {canRemember && language && resolvedVoiceId && (
                  <PinToggle
                    type="button"
                    disabled={rowBusy}
                    aria-label={remembered ? 'Unpin' : 'Pin'}
                    aria-pressed={remembered}
                    $pinned={remembered}
                    data-tooltip-id={DUB_ROW_TOOLTIP_ID}
                    data-tooltip-content={
                      remembered
                        ? `Remove as ${speaker.name}’s default for ${language.caption}. The voice is auto-picked next time.`
                        : `Make this ${speaker.name}’s default for ${language.caption}, for every dub.`
                    }
                    onClick={() =>
                      setLanguageDefault(
                        language.id,
                        remembered ? null : resolvedVoiceId,
                      )
                    }
                  >
                    <PinIcon filled={remembered} />
                  </PinToggle>
                )}
                {/* PLAT-12023 — with a direction to hand, the play button
                    previews this speaker's words with it; otherwise the
                    stored sample, as before. */}
                {resolvedVoiceId &&
                  language &&
                  (preview ? (
                    <DirectedPreviewButton
                      aiVoiceId={resolvedVoiceId}
                      language={language.value}
                      preview={preview}
                      disabled={rowBusy}
                    />
                  ) : (
                    <LanguageSamplePlayButton
                      aiVoiceId={resolvedVoiceId}
                      language={language.value}
                      disabled={rowBusy}
                    />
                  ))}
              </div>
            </>
          ) : (
            // No voices for this language yet — there's no picker to host the
            // action, so keep a direct entry point here.
            !readOnly && (
              <ManageLink
                type="button"
                onClick={() => onManageSpeaker(speaker)}
                disabled={rowBusy}
              >
                Manage voices →
              </ManageLink>
            )
          )}
        </div>
      </Row>
    );
  },
);

// Borderless: the frame is drawn by the popover's scroll container around
// it, so a list taller than the popover keeps its rounded bottom corners
// instead of having them scrolled out of view.
const List = styled.div`
  display: flex;
  flex-direction: column;
`;

// Every row is the height of a row with a picker, no more and no less: a
// speaker without a voice gets the whole middle column for its one-line hint
// and the action at the right edge, instead of a wrapped hint beside an
// empty picker column.
const Row = styled.div<{ $noVoice?: boolean }>`
  display: grid;
  grid-template-columns:
    28px minmax(0, 1fr)
    ${p => (p.$noVoice ? 'auto' : '400px')};
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  min-height: 51px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }

  .body {
    display: flex;
    /* A speaker with no voice says so on the name's own line, so the row is
       no taller than one with a picker. */
    flex-direction: ${p => (p.$noVoice ? 'row' : 'column')};
    align-items: ${p => (p.$noVoice ? 'baseline' : 'stretch')};
    gap: ${p => (p.$noVoice ? '8px' : '2px')};
    min-width: 0;
  }
  .name {
    color: var(--foreground);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    line-height: var(--leading-text-sm);
  }
  .hint {
    color: var(--text-muted);
    font-size: var(--text-xs);
    line-height: var(--leading-text-xs);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .controls {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }
  .select-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  /* The picker flexes; the sample play button keeps its fixed circular size. */
  .select-row > *:first-child {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 640px) {
    grid-template-columns: 28px minmax(0, 1fr);
    .controls {
      grid-column: 1 / -1;
    }
  }
`;

// Beside the picker: the pushpin that promotes this dub's pick to the
// speaker's pin for the language. The same quiet round control as the sample
// play beside it; pinned is a state, so it is carried in gold (the creative /
// selection colour), never green.
const PinToggle = styled.button<{ $pinned: boolean }>`
  ${samplePlayCss}
  cursor: pointer;
  ${p => p.$pinned && 'color: var(--accent);'}

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: ${p => (p.$pinned ? 'var(--accent)' : 'var(--foreground)')};
  }
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

// The line above an unassigned-voice list: what the rows below it are for.
// `$spaced` when it follows the mapped list rather than opening the body.
// PLAT-12041 — the note sits inside the same bordered box as the rows, so it
// takes the rows' side padding: text and rows share one left edge.
const Lead = styled.p<{ $spaced?: boolean }>`
  margin: 0;
  padding: ${p => (p.$spaced ? '16px 14px 12px' : '12px 14px')};
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: 1.4;
`;

// A transcript voice awaiting a speaker. Same rhythm as a mapped Row, with the
// assign action where that row's picker sits.
const UnassignedRow = styled(Row)`
  grid-template-columns: 28px minmax(0, 1fr) auto;

  @media (max-width: 640px) {
    grid-template-columns: 28px minmax(0, 1fr);
  }
`;

// PLAT-12041 — the Secondary treatment (docs/design-system.md, Button
// Hierarchy): one of these per unassigned voice, and Generate is the modal's
// only primary action.
const AssignButton = styled.button`
  justify-self: end;
  padding: 6px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const ManageLink = styled.button`
  align-self: flex-start;
  border: none;
  background: transparent;
  padding: 0;
  color: var(--primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
